import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle, AlertCircle, Mail, ArrowRight } from "lucide-react";
import Button from "./ui/Button";
import { profileService } from "../lib/profileService";
import { useAuth } from "../context/AuthContext";
import { getCanonicalAffiliateName } from "../lib/affiliateIdentity";

const VerifyEmailChange = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    const verifyEmailChange = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const token = urlParams.get("token");

        if (!token) {
          setStatus("error");
          setMessage("Invalid verification link. Token is missing.");
          return;
        }

        const result = await profileService.verifyEmailChange(token);

        if (result.success) {
          setStatus("success");
          setMessage(result.message);
          setNewEmail(result.newEmail);
        } else {
          setStatus("error");
          setMessage(result.error || "Email verification failed.");
        }
      } catch (error) {
        console.error("Email verification error:", error);
        setStatus("error");
        setMessage(error.message || "An error occurred during verification.");
      }
    };

    verifyEmailChange();
  }, [location.search]);

  const handleContinue = () => {
    if (user) {
      // Navigate to affiliate settings
      navigate(
        `/affiliate/${getCanonicalAffiliateName(profile, user)}/settings`
      );
    } else {
      // Navigate to login
      navigate("/login");
    }
  };

  const handleResendVerification = async () => {
    try {
      // This would be implemented if we need to resend verification emails
      setMessage(
        "Please request a new email change from your account settings."
      );
    } catch (error) {
      console.error("Error resending verification:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#B8460E]/10 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute bottom-0 right-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="relative z-10 max-w-md w-full">
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              {status === "loading" && (
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B8460E]"></div>
              )}
              {status === "success" && (
                <CheckCircle className="h-12 w-12 text-green-500" />
              )}
              {status === "error" && (
                <AlertCircle className="h-12 w-12 text-red-500" />
              )}
            </div>

            <h1 className="text-2xl font-bold text-white mb-2">
              {status === "loading" && "Verifying Email Change..."}
              {status === "success" && "Email Updated Successfully!"}
              {status === "error" && "Email Change Failed"}
            </h1>

            <p className="text-gray-300 text-sm leading-relaxed">
              {status === "loading" &&
                "Please wait while we verify your new email address."}
              {status === "success" && (
                <>
                  Your email has been successfully changed to{" "}
                  <span className="text-[#B8460E] font-medium">{newEmail}</span>
                  . You can now use this email to log in to your account.
                </>
              )}
              {status === "error" && message}
            </p>
          </div>

          {status === "success" && (
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-green-300 font-medium text-sm">
                      What's Next?
                    </h3>
                    <p className="text-green-200 text-xs mt-1">
                      Your account is now using your new email address. You can
                      continue using your account normally.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleContinue}
                className="w-full bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white font-medium py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Continue to Settings
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-red-300 font-medium text-sm">
                      Verification Failed
                    </h3>
                    <p className="text-red-200 text-xs mt-1">
                      The verification link may be expired or invalid. Try
                      requesting a new email change from your account settings.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handleContinue}
                  className="w-full bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-6 rounded-xl transition-all duration-300 border border-white/20"
                >
                  Go to Account Settings
                </Button>

                <Button
                  onClick={() => navigate("/login")}
                  variant="secondary"
                  className="w-full bg-transparent hover:bg-white/5 text-gray-300 font-medium py-3 px-6 rounded-xl transition-all duration-300 border border-white/10"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-gray-400 text-xs">
            Need help?{" "}
            <a
              href="mailto:support@olympusaffiliates.com"
              className="text-[#B8460E] hover:text-[#8B3A0E] transition-colors duration-200"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailChange;
